import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Home, Users, Award, Bus, UserCheck, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, XCircle, MailCheck, CircleDollarSign, CheckCircle, Loader2, StickyNote, BellRing } from 'lucide-react';

const StudentTable = ({
  students = [],
  onEdit,
  onDelete,
  onView,
  isLoading = false,
  userPermissions = {},
  orderBy = 'student_id',
  orderDirection = 'asc',
  onSort,
  showSelection = false,
  selectedStudents = new Set(),
  onSelectStudent,
  onSelectAll,
  showEmailStatus = false
}) => {
  const { t } = useTranslation();

  // Sortable header component
  const SortableHeader = ({ field, children }) => {
    const isSorted = orderBy === field;
    const direction = isSorted ? orderDirection : null;

    const getAriaLabel = () => {
      if (isSorted) {
        return direction === 'asc'
          ? t('students.sortedAscending', `Sorted ascending`)
          : t('students.sortedDescending', `Sorted descending`);
      }
      return t('students.clickToSort', `Click to sort`);
    };

    return (
      <TableHead
        className="cursor-pointer select-none hover:bg-muted/50 transition-colors"
        onClick={() => onSort(field)}
        role="button"
        tabIndex={0}
        aria-label={`${children} - ${getAriaLabel()}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSort(field);
          }
        }}
      >
        <div className="flex items-center gap-1">
          {children}
          {isSorted ? (
            direction === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : (
              <ArrowDown className="h-4 w-4" />
            )
          ) : (
            <ArrowUpDown className="h-4 w-4 opacity-30" />
          )}
        </div>
      </TableHead>
    );
  };

  // Determine which columns to show based on permissions
  const canViewContactInfo = ['ADMIN', 'OFFICE'].includes(userPermissions.userType);
  const canViewFinancials = ['ADMIN', 'OFFICE'].includes(userPermissions.userType);
  const canViewFullDetails = ['ADMIN', 'OFFICE'].includes(userPermissions.userType);
  const getStatusBadge = (status) => {
    const statusMap = {
      'ACTIVE': { variant: 'default', label: t('status.ACTIVE') },
      'COMPLETED': { variant: 'outline', label: t('status.COMPLETED') },
      'ARCHIVED': { variant: 'secondary', label: t('status.ARCHIVED') },
      'CANCELLED': { variant: 'destructive', label: t('status.CANCELLED') }
    };
    
    const statusInfo = statusMap[status] || { variant: 'secondary', label: status || 'Unknown' };
    
    return (
      <Badge variant={statusInfo.variant}>
        {statusInfo.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mb-3" />
          <p className="text-sm font-medium">{t('students.loadingStudents')}</p>
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="rounded-md border">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="bg-muted rounded-full p-4 mb-4">
            <Users className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-base font-medium text-foreground mb-1">{t('students.noStudentsFound')}</p>
          <p className="text-sm text-muted-foreground">{t('students.adjustFilters')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border h-full flex flex-col">
      <div className="flex-1 overflow-auto">
        <Table>
        <TableCaption>
          {t('students.showingStudents', { count: students.length })} {t('students.fromColaiste')}
        </TableCaption>
        <TableHeader>
          <TableRow>
            {showSelection && (
              <TableHead className="w-12">
                <Checkbox
                  checked={students.length > 0 && students.every(s => selectedStudents.has(s.uuid))}
                  onCheckedChange={onSelectAll}
                  aria-label={t('students.selectAll', 'Select all')}
                />
              </TableHead>
            )}
            <SortableHeader field="student_id">
              {t('students.studentId')}
            </SortableHeader>
            <SortableHeader field="last_name">
              {t('students.name')}
            </SortableHeader>
            {canViewContactInfo && (
              <TableHead>{t('students.contact')}</TableHead>
            )}
            <TableHead>{t('students.interviewScore')}</TableHead>
            <TableHead>{t('students.course')}</TableHead>
            <TableHead>{t('students.house')}</TableHead>
            <TableHead className="text-center">{t('students.indicators')}</TableHead>
            <TableHead>{t('students.status')}</TableHead>
            {showEmailStatus && (
              <TableHead className="text-center">{t('students.emailStatus', 'Email')}</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map((student, index) => {
            const isSelected = selectedStudents.has(student.uuid);
            const isEvenRow = index % 2 === 1;
            return (
            <TableRow
              key={student.id}
              className={`hover:bg-muted/50 transition-colors ${
                showSelection && isSelected ? 'bg-blue-50' : isEvenRow ? 'bg-muted/20' : ''
              } ${!showSelection ? 'cursor-pointer' : ''}`}
              onClick={showSelection ? undefined : () => onView?.(student)}
            >
              {showSelection && (
                <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onSelectStudent?.(student.uuid)}
                    aria-label={t('students.selectStudent', `Select ${student.full_name || student.first_name}`)}
                  />
                </TableCell>
              )}
              <TableCell className="font-mono text-sm">
                {student.student_id || `ST${student.id?.toString().padStart(4, '0')}`}
              </TableCell>
              <TableCell>
                <div>
                  <div className="font-medium">
                    {student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim()}
                  </div>
                  {student.school_year_name && (
                    <div className="text-xs text-muted-foreground">{student.school_year_name}</div>
                  )}
                </div>
              </TableCell>
              {canViewContactInfo && (
                <TableCell>
                  <div className="space-y-1">
                    {student.parent_email && (
                      <div className="text-sm">{student.parent_email}</div>
                    )}
                    {student.parent_phone && (
                      <div className="text-sm text-muted-foreground">{student.parent_phone}</div>
                    )}
                    {!student.parent_email && !student.parent_phone && (
                      <span className="text-muted-foreground text-sm">{t('students.noContactInfo')}</span>
                    )}
                  </div>
                </TableCell>
              )}
              <TableCell>
                <div className="text-sm text-center">
                  {student.interview_score !== null && student.interview_score !== undefined ? (
                    <span className="font-medium">{student.interview_score}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {student.course?.name || student.course_name || '—'}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {student.house?.name || student.house_name || '—'}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-center gap-2">
                  {(student.house?.name || student.house_name) && (
                    <div className="relative group">
                      <Home className="h-4 w-4 text-purple-600" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        {student.house?.name || student.house_name}
                      </div>
                    </div>
                  )}
                  {student.is_cinnire && (
                    <div className="relative group">
                      <Award className="h-4 w-4 text-amber-500" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        {t('students.cinnire')}
                      </div>
                    </div>
                  )}
                  {student.travel_required && (
                    <div className="relative group">
                      <Bus className="h-4 w-4 text-blue-500" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        {t('students.travelRequired')}
                      </div>
                    </div>
                  )}
                  {student.has_friends && (
                    <div className="relative group">
                      <UserCheck className="h-4 w-4 text-green-500" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        {t('students.hasFriends')}
                      </div>
                    </div>
                  )}
                  {student.upload_status === 'IMPORT_WARNING' && (
                    <div className="relative group cursor-pointer" onClick={(e) => {
                      e.stopPropagation();
                      onView?.(student);
                    }}>
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        {t('students.importWarning', 'Import Warning')}: {student.upload_issues?.length || 0} {t('students.issues', 'issues')}
                      </div>
                    </div>
                  )}
                  {student.upload_status === 'IMPORT_ERROR' && (
                    <div className="relative group cursor-pointer" onClick={(e) => {
                      e.stopPropagation();
                      onView?.(student);
                    }}>
                      <XCircle className="h-4 w-4 text-red-600" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        {t('students.importError', 'Import Error')}: {student.upload_issues?.length || 0} {t('students.issues', 'issues')}
                      </div>
                    </div>
                  )}
                  {student.upload_status === 'ACTION_REQUIRED' && (
                    <div className="relative group mt-1 inline-block cursor-pointer" onClick={(e) => { e.stopPropagation(); onView?.(student); }}>
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                        <BellRing className="h-3 w-3" />
                        {t('students.actionRequired', 'Action Required')}
                      </span>
                      {student.upload_issues?.length > 0 && (
                        <div className="absolute top-full left-0 mt-1 px-2.5 py-2 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-56 w-max">
                          <div className="space-y-1">
                            {student.upload_issues.map((issue, i) => (
                              <div key={i}>{issue.message}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Outstanding Balance Indicator - Only visible to ADMIN/OFFICE */}
                  {canViewFinancials && student.balance_due && parseFloat(student.balance_due) > 0 && (
                    <div className="relative group">
                      <CircleDollarSign className="h-4 w-4 text-amber-500" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        {t('students.payment.outstandingBalance', 'Outstanding')}: €{parseFloat(student.balance_due).toFixed(2)}
                      </div>
                    </div>
                  )}
                  {/* Fully Paid Indicator - Only visible to ADMIN/OFFICE */}
                  {canViewFinancials && student.balance_due && parseFloat(student.balance_due) <= 0 && parseFloat(student.total_paid || 0) > 0 && (
                    <div className="relative group">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        {t('students.payment.fullyPaid', 'Fully Paid')}
                      </div>
                    </div>
                  )}
                  {/* Staff Note Indicator - Only visible to ADMIN/OFFICE */}
                  {canViewFullDetails && student.notes && (
                    <div className="relative group">
                      <StickyNote className="h-4 w-4 text-amber-500" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-48 whitespace-normal text-center">
                        {student.notes.length > 120 ? `${student.notes.slice(0, 120)}…` : student.notes}
                      </div>
                    </div>
                  )}
                  {!student.is_cinnire && !student.travel_required && !student.has_friends && !(student.house?.name || student.house_name) && student.upload_status !== 'IMPORT_WARNING' && student.upload_status !== 'IMPORT_ERROR' && student.upload_status !== 'ACTION_REQUIRED' && !(canViewFinancials && student.balance_due) && !(canViewFullDetails && student.notes) && (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </div>
              </TableCell>
              <TableCell>{getStatusBadge(student.status)}</TableCell>
              {showEmailStatus && (
                <TableCell className="text-center">
                  {student.email_sent && (
                    <div className="relative group inline-flex">
                      <MailCheck className="h-5 w-5 text-green-600" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        {t('students.emailSent', 'Email sent')}
                      </div>
                    </div>
                  )}
                </TableCell>
              )}
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
};

export default StudentTable;